/**
 * Keeps the screen awake during play.
 *
 * Without it a phone dims and locks partway through a level, because the player is
 * touching the screen only intermittently and the OS reads that as idle.
 *
 * Entirely best-effort: the API is absent on older browsers and the request can simply be
 * refused (low battery, for one). Both cases are silent — a game that popped an error
 * because it could not stop the screen dimming would be worse than one that dims.
 */

import { useEffect } from 'react';

type WakeLockSentinelLike = { release: () => Promise<void> };
type WakeLockLike = { request: (type: 'screen') => Promise<WakeLockSentinelLike> };

function getWakeLock(): WakeLockLike | undefined {
  return (navigator as Navigator & { wakeLock?: WakeLockLike }).wakeLock;
}

export function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;

    const wakeLock = getWakeLock();
    if (!wakeLock) return;

    let sentinel: WakeLockSentinelLike | null = null;
    let cancelled = false;

    const acquire = async () => {
      try {
        const next = await wakeLock.request('screen');
        // The effect may have been torn down while the request was in flight.
        if (cancelled) {
          void next.release();
          return;
        }
        sentinel = next;
      } catch {
        // Refused. Nothing to tell the player.
      }
    };

    const release = () => {
      const held = sentinel;
      sentinel = null;
      if (held) void held.release().catch(() => {});
    };

    // The browser drops the lock whenever the page is hidden, so it has to be taken again
    // on return — otherwise the screen sleeps after the first alt-tab.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void acquire();
      else release();
    };

    void acquire();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      release();
    };
  }, [active]);
}
